import axios from '@/core/axios';

import {
  CrmTask,
  CrmTaskCounters,
  CrmTaskPriorityOption,
  CrmTaskRecurrenceOptions,
  CrmTaskRecurrencePayload,
  CrmTaskRecurrenceRuleOption,
  CrmTaskStatusOption,
  CrmTaskableType,
  CrmTasksList
} from './crm-tasks.types';

export class CrmTasksService {
  list(params: Record<string, unknown> = {}): Promise<CrmTasksList> {
    return axios
      .get<CrmTasksList>('/crm-tasks', { params: { limit: 500, ...params } })
      .then(resp => resp?.data || { values: [], count: 0 });
  }

  get(taskId: number | string): Promise<CrmTask> {
    return axios.get<CrmTask>(`/crm-tasks/${taskId}`).then(resp => resp?.data);
  }

  create(task: Partial<CrmTask>): Promise<CrmTask> {
    return axios.post<CrmTask>('/crm-tasks', { crm_task: task }).then(resp => resp?.data);
  }

  /* Crée une série récurrente (éditeur façon Outlook) : génère et persiste en une
   * fois toutes les occurrences respectant la règle choisie. */
  createRecurring(recurrence: CrmTaskRecurrencePayload): Promise<{ tasks: CrmTask[]; count: number }> {
    return axios
      .post<{ tasks: CrmTask[]; count: number }>('/crm-tasks/recurring', { crm_task_recurrence: recurrence })
      .then(resp => resp?.data);
  }

  update(taskId: number | string, task: Partial<CrmTask>): Promise<CrmTask> {
    if (!taskId) throw new Error('Missing task id in update');
    return axios.put<CrmTask>(`/crm-tasks/${taskId}`, { crm_task: task }).then(resp => resp?.data);
  }

  /* Édition façon Outlook "cette occurrence et toutes les suivantes" : contenu
   * (titre/notes/priorité/assigné(e)/rattachement) appliqué aux occurrences
   * futures encore ouvertes, échéances décalées du même delta côté backend. */
  updateSeries(taskId: number | string, task: Partial<CrmTask>): Promise<CrmTask> {
    if (!taskId) throw new Error('Missing task id in updateSeries');
    return axios.put<CrmTask>(`/crm-tasks/${taskId}/update-series`, { crm_task: task }).then(resp => resp?.data);
  }

  delete(taskId: number | string): Promise<void> {
    if (!taskId) throw new Error('Missing task id in delete');
    return axios.delete<void>(`/crm-tasks/${taskId}`).then(resp => resp?.data);
  }

  complete(taskId: number | string): Promise<CrmTask> {
    return axios.post<CrmTask>(`/crm-tasks/${taskId}/complete`).then(resp => resp?.data);
  }

  reopen(taskId: number | string): Promise<CrmTask> {
    return axios.post<CrmTask>(`/crm-tasks/${taskId}/reopen`).then(resp => resp?.data);
  }

  cancel(taskId: number | string): Promise<CrmTask> {
    return axios.post<CrmTask>(`/crm-tasks/${taskId}/cancel`).then(resp => resp?.data);
  }

  /* Reschedule (snooze) : due_at null/undefined retire l'échéance */
  reschedule(taskId: number | string, dueAt: string | null): Promise<CrmTask> {
    return axios.post<CrmTask>(`/crm-tasks/${taskId}/reschedule`, { due_at: dueAt }).then(resp => resp?.data);
  }

  reassign(taskId: number | string, assignedToId: number): Promise<CrmTask> {
    return axios
      .post<CrmTask>(`/crm-tasks/${taskId}/reassign`, { assigned_to_id: assignedToId })
      .then(resp => resp?.data);
  }

  assignTeam(taskId: number | string, assignedTeamId: number): Promise<CrmTask> {
    return axios
      .post<CrmTask>(`/crm-tasks/${taskId}/assign-team`, { assigned_team_id: assignedTeamId })
      .then(resp => resp?.data);
  }

  counters(): Promise<CrmTaskCounters> {
    return axios
      .get<CrmTaskCounters>('/crm-tasks/counters')
      .then(resp => resp?.data || { overdue: 0, due_today: 0, upcoming: 0, no_due_date: 0 });
  }

  statuses(): Promise<CrmTaskStatusOption[]> {
    return axios.get<CrmTaskStatusOption[]>('/crm-tasks/statuses').then(resp => resp?.data || []);
  }

  priorities(): Promise<CrmTaskPriorityOption[]> {
    return axios.get<CrmTaskPriorityOption[]>('/crm-tasks/priorities').then(resp => resp?.data || []);
  }

  recurrenceRules(): Promise<CrmTaskRecurrenceRuleOption[]> {
    return axios.get<CrmTaskRecurrenceRuleOption[]>('/crm-tasks/recurrence-rules').then(resp => resp?.data || []);
  }

  recurrenceOptions(): Promise<CrmTaskRecurrenceOptions> {
    return axios
      .get<CrmTaskRecurrenceOptions>('/crm-tasks/recurrence-options')
      .then(resp => resp?.data || { end_types: [], month_modes: [], max_occurrences: 500, max_horizon_years: 2 });
  }

  taskableTypes(): Promise<CrmTaskableType[]> {
    return axios.get<CrmTaskableType[]>('/crm-tasks/taskable-types').then(resp => resp?.data || []);
  }
}

export const crmTasksService = new CrmTasksService();
